/* eslint-disable @typescript-eslint/prefer-reduce-type-parameter */
/* eslint-disable sort-exports/sort-exports */
import { toEnum } from '@tryiris-ai/js-types';
import { PrefKeys } from './constants.js';
import { GridLines, Tool } from './types.js';
import { toGridLines, toTool } from './utils.js';
import { Command } from '#~/command/index.js';
import type { UsePrefState } from '#~/preferences/hooks.js';
import { usePrefState } from '#~/preferences/hooks.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';

/**
 * Grid line style.
 *
 * @returns Grid line style.
 */
export const useGridLinesPref = (): UsePrefState<GridLines> =>
  usePrefState(PrefKeys.gridLines, GridLines.None, {
    deserialize: toGridLines
  });

/** Tool variant. */
export type ToolCommandMap = Record<Tool, Command>;

/**
 * Selected movement tool variant.
 *
 * @returns Selected movement tool variant.
 */
export const useToolVariants = (): [ToolCommandMap, (tool: Tool, next: Command) => void] => {
  const [toolVariant, setToolVariant] = usePrefState(PrefKeys.toolVariant, '{}');

  const set = (tool: Tool, next: Command): void => {
    const v = <Record<Tool, Command>>JSON.parse(toolVariant);

    v[tool] = next;

    setToolVariant(JSON.stringify(v));
  };

  const parsed: ToolCommandMap = Object.entries(JSON.parse(toolVariant))
    .map(([k, v]): [Tool, Command] => [toTool(k.trim())!, toEnum(Command, v)!])
    .reduce<ToolCommandMap>(
      (accumulator, [k, v]) => {
        accumulator[k] = v;

        return accumulator;
      },
      <ToolCommandMap>(<unknown>{})
    );

  return [parsed, set];
};

/**
 * Selected movement tool.
 *
 * @returns Selected movement tool.
 */
export const useToolPref = (): UsePrefState<Tool> => {
  const accessor = usePrefState(PrefKeys.tool, Tool.Center, {
    deserialize: toTool
  });

  const { enabled: hasAutoTracking } = useFeatureFlag(PostHogFeatureFlag.AUTOTRACKING, {
    defaultValue: false
  });

  return [
    accessor[0],
    (next: unknown): void => {
      const isAutoTracking = next === Tool.Tracking;

      if (isAutoTracking && !hasAutoTracking) {
        return;
      }

      accessor[1](next);
    }
  ];
};
